/**
 * Home: the greeting, the composer and three starters, centred a little above
 * the middle as in the prototype.
 *
 * On a phone the composer docks at the foot, as a chat's does, with the
 * starters in one sideways row above it and the greeting centred in what is
 * left, so the keyboard coming up moves nothing but the greeting.
 *
 * Sending from here makes the chat first, then starts its first turn in the
 * turn store and moves to it — the chat page picks the reply up already
 * streaming. `?project=<id>` preselects a project; the project page's "New
 * chat" links here that way.
 *
 * The ghost in the header makes the chats started here temporary
 * (hooks/temporaryChats.ts): no project, no memory, never listed. It stays on
 * from one visit to the next, until a reload.
 *
 * The prototype's starters were an LMS demo's ("Add a knowledge check to
 * lesson 3"), pointing at files that do not exist. These three need no file
 * and suit the people Mauvely is for, and each sends a fuller prompt than its
 * label so the first reply can ask the right questions.
 */

import { useEffect, useMemo, useState } from 'react';
import { DEFAULT_EFFORT } from '@wire';
import { ApiError, createChat, type Attachment, type Effort } from '../api';
import { Composer } from '../components/composer/Composer';
import { greeting } from '../components/composer/usageText';
import { MainHeader, useShell } from '../components/Shell';
import { Ph } from '../components/ui';
import { stashChat, toSummary } from '../hooks/chatStash';
import { markTemporary } from '../hooks/temporaryChats';
import { setResearch, startTurn } from '../hooks/useTurns';
import { setPendingVoice } from '../hooks/voiceStart';
import { unlockAudio } from '../audio/speaker';
import { sharedImages } from '../android';
import { useRouter } from '../router';
import { useApp } from '../state';

const STARTERS = [
  {
    icon: 'path',
    label: 'Plan a short course',
    text: 'Help me plan a short course. Ask me what it covers and who it is for, then suggest an outline with lessons and rough timings.'
  },
  {
    icon: 'list-checks',
    label: 'Write a knowledge check',
    text: 'Write a short knowledge check for a lesson: four multiple-choice questions with three options each. Ask me for the topic first.'
  },
  {
    icon: 'envelope-simple',
    label: 'Draft a welcome email',
    text: 'Draft a short welcome email for people starting a new course. Ask me for the course name, the start date and who it is from.'
  }
];

/** The ghost's state. Module memory, so it survives leaving Home, not a reload. */
let temporaryNext = false;

export function HomePage() {
  const { me, catalogue, projects, upsertChat, touchChat, patchMe, refreshMe } = useApp();
  const { query, navigate } = useRouter();
  const { narrow } = useShell();

  const queryProject = query.get('project');
  const [projectId, setProjectId] = useState<string | null>(queryProject);
  useEffect(() => setProjectId(queryProject), [queryProject]);

  // Text shared to the Android app arrives as ?q=: it starts the draft, once.
  // Images arrive as ?shared=1, and the app hands them over when asked.
  const [shared] = useState(() => query.get('q') ?? '');
  const [sharedFiles, setSharedFiles] = useState<File[]>();
  useEffect(() => {
    if (query.get('shared') === '1') {
      void sharedImages().then((files) => {
        if (files.length) setSharedFiles(files);
      });
    }
    if (!query.has('q') && !query.has('shared')) return;
    const rest = new URLSearchParams(query);
    rest.delete('q');
    rest.delete('shared');
    navigate(rest.size ? `/?${rest}` : '/', { replace: true });
    // Only the query the page was opened with matters, and only once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The account's default, unless the catalogue no longer offers it here.
  const [picked, setPicked] = useState<string | null>(null);
  const model = useMemo(() => {
    if (picked) return picked;
    const preferred = me?.settings.defaultModel ?? '';
    if (!catalogue) return preferred;
    const offered = catalogue.models.some((m) => m.id === preferred && m.group !== 'device');
    return offered ? preferred : catalogue.defaultModel;
  }, [picked, me?.settings.defaultModel, catalogue]);

  const [pickedEffort, setPickedEffort] = useState<Effort | null>(null);
  const effort = pickedEffort ?? me?.settings.defaultEffort ?? DEFAULT_EFFORT;

  const [temporary, setTemporary] = useState(temporaryNext);
  const toggleTemporary = () => {
    temporaryNext = !temporary;
    setTemporary(temporaryNext);
  };

  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  /** Research, for the chat the next message starts. */
  const [research, setResearchOn] = useState(false);

  const start = async (text: string, attachments: Attachment[]) => {
    setError(null);
    setStarting(true);
    try {
      // A project that has since been deleted is dropped rather than refused.
      const inProject = projectId && projects.some((p) => p.id === projectId) ? projectId : null;
      const chat = await createChat(
        temporary ? { projectId: null, model, effort, temporary: true } : { projectId: inProject, model, effort }
      );
      // Before anything lists it: the registry is what keeps it out.
      if (temporary) markTemporary(chat.id);
      stashChat(chat);
      upsertChat(toSummary(chat));
      if (research) setResearch(chat.id, true);
      startTurn({
        chat: toSummary(chat),
        kind: 'turn',
        input: { text, attachments },
        app: { upsertChat, touchChat, patchMe, refreshMe }
      });
      navigate(`/chat/${chat.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ai_disabled') void refreshMe();
      setError(err instanceof ApiError && err.status !== 0 ? err.message : 'Assist did not answer. Try again.');
      throw err;
    } finally {
      setStarting(false);
    }
  };

  /** Voice from Home: the chat is made, and voice mode opens in it. The
   *  audio starts here, inside the click. */
  const startVoice = () => {
    const context = unlockAudio();
    setError(null);
    setStarting(true);
    void (async () => {
      try {
        const inProject = projectId && projects.some((p) => p.id === projectId) ? projectId : null;
        const chat = await createChat(
          temporary ? { projectId: null, model, effort, temporary: true } : { projectId: inProject, model, effort }
        );
        if (temporary) markTemporary(chat.id);
        stashChat(chat);
        upsertChat(toSummary(chat));
        setPendingVoice(chat.id, context);
        navigate(`/chat/${chat.id}`);
      } catch (err) {
        void context.close().catch(() => undefined);
        if (err instanceof ApiError && err.code === 'ai_disabled') void refreshMe();
        setError(err instanceof ApiError && err.status !== 0 ? err.message : 'Assist did not answer. Try again.');
      } finally {
        setStarting(false);
      }
    })();
  };

  const firstName = me?.user.firstName ?? '';

  const heading = (
    <>
      <h1
        style={{
          margin: 0,
          fontFamily: 'var(--font-display)',
          fontWeight: 800,
          fontSize: narrow ? 28 : 40,
          lineHeight: 1.02,
          letterSpacing: 'normal',
          color: 'var(--text-strong)',
          textAlign: 'center',
          padding: narrow ? (temporary ? '0 16px 10px' : '0 16px') : temporary ? '0 24px 10px' : '0 24px 26px'
        }}
      >
        {temporary ? (
          'Temporary chat.'
        ) : (
          <>
            {greeting(new Date().getHours())}
            {firstName ? `, ${firstName}.` : '.'}
          </>
        )}
      </h1>
      {temporary ? (
        <p
          style={{
            margin: 0,
            padding: narrow ? '0 16px' : '0 24px 22px',
            textAlign: 'center',
            fontSize: 14,
            color: 'var(--text-muted)'
          }}
        >
          Not saved to your chats, and memory is off.
        </p>
      ) : null}
    </>
  );

  const composer = (
    <div style={{ flex: 'none', padding: narrow ? '8px 16px max(16px, env(safe-area-inset-bottom))' : '0 24px' }}>
      <Composer
        variant="home"
        model={model}
        onModelChange={setPicked}
        effort={effort}
        onEffortChange={setPickedEffort}
        projectId={temporary ? null : projectId}
        {...(temporary ? {} : { onProjectChange: setProjectId })}
        onSend={start}
        autoFocus={!narrow}
        initialDraft={shared}
        incomingFiles={sharedFiles}
        temporary={temporary}
        onVoice={starting ? undefined : startVoice}
        research={research}
        onResearch={setResearchOn}
      />
      {error ? (
        <div
          role="alert"
          style={{
            maxWidth: 700,
            margin: '10px auto 0',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13.5,
            color: 'var(--text-muted)'
          }}
        >
          <Ph name="warning-circle" size={15} color="var(--danger-500)" />
          {error}
        </div>
      ) : null}
    </div>
  );

  const starters = (
    <div
      className={narrow ? 'scroll-row' : undefined}
      style={
        narrow
          ? { flex: 'none', display: 'flex', gap: 8, padding: '4px 16px' }
          : { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8, padding: '6px 24px 0' }
      }
    >
      {STARTERS.map((s) => (
        <button
          key={s.label}
          type="button"
          className="bare hov-soft"
          disabled={starting}
          onClick={() => void start(s.text, []).catch(() => {})}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '8px 14px',
            borderRadius: 999,
            border: '1px solid var(--border-strong)',
            fontSize: 13,
            color: 'var(--text-muted)'
          }}
        >
          <Ph name={s.icon} size={14} />
          {s.label}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <MainHeader>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="bare hov-tint"
          aria-label="Temporary chat"
          title="Temporary chat"
          aria-pressed={temporary}
          onClick={toggleTemporary}
          style={{
            width: 32,
            height: 30,
            flex: 'none',
            display: 'grid',
            placeItems: 'center',
            borderRadius: 'var(--radius-chrome)',
            color: temporary ? 'var(--primary)' : 'var(--text-muted)'
          }}
        >
          <Ph name="ghost" size={17} />
        </button>
      </MainHeader>

      {/* The composer holds the draft, so it keeps its place in this list
          whichever layout is drawn: crossing 760px does not lose it. */}
      <div className="scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {narrow ? (
          <div className="scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1 }} />
            {heading}
            <div style={{ flex: 1 }} />
          </div>
        ) : (
          <>
            <div style={{ flex: 1 }} />
            {heading}
          </>
        )}
        {narrow ? starters : null}
        {composer}
        {narrow ? null : (
          <>
            {starters}
            <div style={{ flex: 1.3 }} />
          </>
        )}
      </div>
    </>
  );
}
